/**
 * Local Admin Overview
 *
 * Compact overview card for the Settings landing page showing:
 * - This-month LLM stats (calls, tokens, cost)
 * - Cloud quota percentage (if linked)
 * - BYOK budget percentage (if enabled)
 * - Month-over-month trend indicator
 * - CSV export button
 *
 * @module @humanizer/studio/components/settings/LocalAdminOverview
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocalAdmin } from '../../hooks/useLocalAdmin';

// ═══════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════

function formatNumber(n: number): string {
  return n.toLocaleString();
}

function formatCost(millicents: number): string {
  if (millicents === 0) return 'Free';
  const dollars = millicents / 100000;
  return dollars < 0.01 ? '< $0.01' : `$${dollars.toFixed(2)}`;
}

function billingPeriod(offset = 0): string {
  const d = new Date();
  d.setMonth(d.getMonth() + offset);
  return d.toISOString().substring(0, 7);
}

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export function LocalAdminOverview() {
  const { getUsageSummary, getCachedQuota, checkByokSpending, exportUsageCSV } = useLocalAdmin();

  const [current, setCurrent] = useState<any>(null);
  const [previous, setPrevious] = useState<any>(null);
  const [quota, setQuota] = useState<any>(null);
  const [byokSpending, setByokSpending] = useState<any>(null);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    const [cur, prev, q, b] = await Promise.all([
      getUsageSummary(billingPeriod()),
      getUsageSummary(billingPeriod(-1)),
      getCachedQuota(),
      checkByokSpending(),
    ]);
    setCurrent(cur);
    setPrevious(prev);
    if (q && !q.error) setQuota(q);
    if (b && !b.error) setByokSpending(b);
  }, [getUsageSummary, getCachedQuota, checkByokSpending]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const result = await exportUsageCSV();
      if (result?.csv) {
        const blob = new Blob([result.csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `humanizer-usage-${billingPeriod()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      // Non-fatal
    } finally {
      setExporting(false);
    }
  }, [exportUsageCSV]);

  if (!current || current.totalCalls === 0) return null;

  // Trend calculation
  const prevCalls = previous?.totalCalls ?? 0;
  const callTrend = prevCalls > 0
    ? Math.round(((current.totalCalls - prevCalls) / prevCalls) * 100)
    : null;

  return (
    <div className="settings-card">
      <div className="settings-card__header">
        <h3 className="settings-card__title">Usage This Month</h3>
        <button
          className="btn btn--ghost btn--sm"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>
      <div className="settings-card__content">
        <div className="usage-stats">
          <div className="usage-stat">
            <span className="usage-stat__value">{formatNumber(current.totalCalls)}</span>
            <span className="usage-stat__label">
              Calls
              {callTrend !== null && (
                <span className={callTrend >= 0 ? 'usage-stat__trend--up' : 'usage-stat__trend--down'}>
                  {' '}{callTrend >= 0 ? '+' : ''}{callTrend}%
                </span>
              )}
            </span>
          </div>
          <div className="usage-stat">
            <span className="usage-stat__value">{formatNumber(current.totalTokens)}</span>
            <span className="usage-stat__label">Tokens</span>
          </div>
          <div className="usage-stat">
            <span className="usage-stat__value">{formatCost(current.totalCostMillicents)}</span>
            <span className="usage-stat__label">Estimated Cost</span>
          </div>
        </div>

        {/* Cloud Quota Mini-Bar */}
        {quota && quota.tokensLimit > 0 && (
          <div className="usage-progress">
            <div className="usage-progress__header">
              <span>Cloud Quota</span>
              <span>{Math.round((quota.tokensUsed / quota.tokensLimit) * 100)}%</span>
            </div>
            <div className="usage-progress__bar">
              <div
                className={`usage-progress__fill${
                  (quota.tokensUsed / quota.tokensLimit) >= 0.9 ? '--danger'
                  : (quota.tokensUsed / quota.tokensLimit) >= 0.75 ? '--warning' : ''
                }`}
                style={{ width: `${Math.min((quota.tokensUsed / quota.tokensLimit) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* BYOK Budget Mini-Bar */}
        {byokSpending && byokSpending.budget > 0 && (
          <div className="usage-progress">
            <div className="usage-progress__header">
              <span>BYOK Budget</span>
              <span>{byokSpending.percentUsed}%</span>
            </div>
            <div className="usage-progress__bar">
              <div
                className={`usage-progress__fill${
                  byokSpending.percentUsed >= 90 ? '--danger'
                  : byokSpending.percentUsed >= 75 ? '--warning' : ''
                }`}
                style={{ width: `${Math.min(byokSpending.percentUsed, 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
